import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Search, Plus, ChevronRight, Folder } from "lucide-react";
import { useJourneyCatalog } from "./use-catalog";
import { JourneyTemplate } from "./types";
import GroupWorkflow from "../../../../assets/images/group.png"

// "Create New Bot Journey" — the category rail and every journey card here
// come straight from GET /v1/bot/journeys: `category` groups them (right now
// "Commerce" and "Banking") in whatever order they first appear in the
// response. Nothing is a fixed taxonomy — a category with zero real journeys
// simply doesn't exist here, there's no "coming soon" placeholder to fake one.

const CreateJourneyModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { loading, journeyTemplates } = useJourneyCatalog();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | "scratch">("scratch");

  const categories = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    journeyTemplates.forEach((jt) => {
      if (jt.category && !seen.has(jt.category)) {
        seen.add(jt.category);
        list.push(jt.category);
      }
    });
    return list;
  }, [journeyTemplates]);

  const matchesSearch = (jt: JourneyTemplate) => {
    const q = search.trim().toLowerCase();
    return !q || jt.name.toLowerCase().includes(q) || jt.description.toLowerCase().includes(q);
  };

  const filteredTemplates = journeyTemplates.filter(matchesSearch);
  const activeCategoryTemplates = activeCategory !== "scratch" ? filteredTemplates.filter((jt) => jt.category === activeCategory) : [];

  if (!isOpen) return null;

  const goToJourney = (jt: JourneyTemplate) => {
    navigate("/automate/bot-studio/customer-journey/new", { state: { journeyId: jt.id, journeyName: jt.name } });
    onClose();
  };

  const handleContinue = () => {
    if (activeCategory !== "scratch") return;
    navigate("/automate/bot-studio/customer-journey/new");
    onClose();
  };

  const JourneyCard: React.FC<{ jt: JourneyTemplate }> = ({ jt }) => (
    <button
      type="button"
      title={jt.description}
      onClick={() => goToJourney(jt)}
      className="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors"
    >
      <span>
        <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100">{jt.name}</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{jt.description}</span>
      </span>
      <span className="flex items-center gap-2 shrink-0 ml-3">
        {jt.status === "template" && (
          <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15 rounded-full px-2 py-0.5">
            Template
          </span>
        )}
        <ChevronRight size={15} className="text-gray-400" />
      </span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <div className="relative w-full max-w-4xl h-[640px] max-h-[85vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex overflow-hidden">
        {/* Left rail */}
        <div className="w-[340px] shrink-0 border-r border-gray-100 dark:border-gray-800 flex flex-col">
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Create New Bot Journey</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Create or manage your bot journeys</p>
          </div>
          <div className="px-6 pb-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Journeys"
                className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20"
              />
            </div>
          </div>
          <p className="px-6 pb-2 text-xs font-semibold text-indigo-600 dark:text-indigo-400">Start Journey When:</p>
          <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
            <button
              type="button"
              onClick={() => setActiveCategory("scratch")}
              className={`flex items-center justify-between w-full px-3.5 py-3 rounded-lg border text-sm font-semibold transition-colors ${activeCategory === "scratch"
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 text-gray-900 dark:text-gray-100"
                  : "border-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
            >
              <span className="flex items-center gap-2">
                <Plus size={15} className="text-indigo-600 dark:text-indigo-400" /> Create a new journey
              </span>
              <Plus size={14} className="text-gray-300 dark:text-gray-600" />
            </button>

            {!loading &&
              categories.map((cat) => {
                const count = journeyTemplates.filter((jt) => jt.category === cat).length;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    className={`flex items-center justify-between w-full px-3.5 py-3 rounded-lg border text-sm font-semibold transition-colors ${activeCategory === cat
                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 text-gray-900 dark:text-gray-100"
                        : "border-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                  >
                    <span className="flex items-center gap-2">
                      <Folder size={14} className="text-gray-400 dark:text-gray-500 shrink-0" /> {cat}
                    </span>
                    <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 shrink-0">{count}</span>
                  </button>
                );
              })}
          </div>
        </div>

        {/* Right pane */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-end px-5 pt-5 shrink-0">
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-8 pb-6">
            {activeCategory === "scratch" ? (
              <div className="h-full flex flex-col">
                <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
                  <h3 className="text-[20px] font-medium text-[#3A3428] dark:text-gray-100">Start from scratch, Customize as you want</h3>
                  <div className="flex items-center justify-center">
                    <img src={GroupWorkflow} alt="Workflow" />
                  </div>
                </div>

                {/* Ready-made journeys from GET /v1/bot/journeys — picking one
                    opens the builder scoped to that journey's own id
                    (?journey=<id> on the triggers/actions fetch, so the
                    palette only shows that journey's blocks and its own
                    trigger gets pre-selected), and seeds the canvas from that
                    journey's own GET /v1/bot/journeys/{id} flow when the
                    backend has one (see builder-page.tsx) — falling back to
                    the same default trigger chain only if it doesn't. */}
                {!loading && filteredTemplates.length > 0 && (
                  <div className="pb-2">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mb-2.5">Or start from a template</p>
                    <div className="space-y-2">
                      {filteredTemplates.map((jt) => (
                        <JourneyCard key={jt.id} jt={jt} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h3 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-gray-100 mb-1">
                  <Folder size={16} className="text-gray-400" /> {activeCategory}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Pick a ready-made journey to start from.</p>

                {loading ? (
                  <div className="space-y-2.5">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="h-[52px] rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
                    ))}
                  </div>
                ) : activeCategoryTemplates.length ? (
                  <div className="space-y-2">
                    {activeCategoryTemplates.map((jt) => (
                      <JourneyCard key={jt.id} jt={jt} />
                    ))}
                  </div>
                ) : (
                  <div className="py-16 text-center text-sm text-gray-400 dark:text-gray-500">No journeys match your search.</div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2.5 px-8 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Save as Draft
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={activeCategory !== "scratch"}
              title={activeCategory !== "scratch" ? "Pick a journey card to continue" : undefined}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm disabled:opacity-60"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateJourneyModal;
